import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Pointer } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

/*
 * One-time nudge: a cursor hand glides in toward the frame's corner and
 * calls out that the component is live, not a static screenshot. Fires
 * once when the signature-components section scrolls into view, then
 * clears itself, so it never becomes a permanent, repeated distraction.
 */
const InteractHint: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const hand = el.querySelector('.cache__interact-hand');
      const tooltip = el.querySelector('.cache__interact-tooltip');

      const tl = gsap.timeline({ paused: true });
      tl.set(el, { opacity: 0, x: 30, y: 30, scale: 0.85 });
      tl.set(tooltip, { opacity: 0, y: 6 });
      tl.to(el, { opacity: 1, x: 0, y: 0, scale: 1, duration: 0.7, ease: 'power3.out' });
      tl.to(hand, { scale: 0.82, duration: 0.12, ease: 'power1.inOut', yoyo: true, repeat: 1 }, '-=0.05');
      tl.to(tooltip, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, '-=0.35');
      tl.to(el, { opacity: 0, duration: 0.6, ease: 'power2.in' }, '+=1.9');

      ScrollTrigger.create({
        trigger: el,
        start: 'top 80%',
        once: true,
        onEnter: () => tl.play(),
      });
    }, ref);

    return () => ctx.revert();
  }, []);

  return (
    <div className="cache__interact-hint" ref={ref} aria-hidden="true">
      <span className="cache__interact-tooltip">You can interact with it</span>
      <Pointer className="cache__interact-hand" size={26} strokeWidth={2} />
    </div>
  );
};

export default InteractHint;
